import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    getIoTDevices,
    exportDeviceRouteCsv,
    createIoTDevice,
} from "../../../api/iotDeviceService";
import WebSocketService from "../../../services/WebSocketService";

const IoTDevicesPage = () => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const wsMapRef = useRef({});

    const [form, setForm] = useState({
        trackingNumber: "",
        latitude: "",
        longitude: "",
    });

    useEffect(() => {
        load();

        return () => {
            Object.values(wsMapRef.current).forEach((ws) => ws.close());
        };
    }, []);

    const stats = useMemo(() => {
        const total = devices.length;
        const online = devices.filter((d) => d.isOnline).length;
        const offline = total - online;
        const withGps = devices.filter((d) => d.latitude && d.longitude).length;

        return { total, online, offline, withGps };
    }, [devices]);

    const connectDeviceWs = (devicesList) => {
        Object.values(wsMapRef.current).forEach((ws) => ws.close());
        wsMapRef.current = {};

        devicesList.forEach((device) => {
            if (!device.trackingNumber) return;

            const ws = new WebSocketService({
                trackingNumber: device.trackingNumber,
            });

            ws.onMessage = (raw) => {
                try {
                    const msg = JSON.parse(raw);

                    if (msg.type !== "iot_update") return;

                    setDevices((prev) =>
                        prev.map((d) =>
                            d.id === msg.deviceId
                                ? {
                                    ...d,
                                    latitude: msg.latitude,
                                    longitude: msg.longitude,
                                    lastUpdated: msg.updatedAt,
                                    isOnline: true,
                                    predicted: msg.predicted,
                                    stats: msg.stats,
                                }
                                : d
                        )
                    );
                } catch (e) {
                    console.error("WS parse error:", e);
                }
            };

            ws.connect();
            wsMapRef.current[device.trackingNumber] = ws;
        });
    };

    const load = async () => {
        try {
            setLoading(true);
            setError("");

            const data = await getIoTDevices();
            const list = Array.isArray(data) ? data : [];

            setDevices(list);
            connectDeviceWs(list);
        } catch (err) {
            console.error("Failed to load devices", err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load IoT devices."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        try {
            setSaving(true);
            setError("");
            setSuccess("");

            if (!form.trackingNumber.trim()) {
                setError("Tracking number is required.");
                return;
            }

            if (!form.latitude || !form.longitude) {
                setError("Latitude and longitude are required.");
                return;
            }

            await createIoTDevice({
                trackingNumber: form.trackingNumber.trim(),
                latitude: Number(form.latitude),
                longitude: Number(form.longitude),
            });

            setForm({ trackingNumber: "", latitude: "", longitude: "" });
            setSuccess("IoT device created successfully.");
            await load();
        } catch (err) {
            console.error(err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Error creating device."
            );
        } finally {
            setSaving(false);
        }
    };

    const exportCsv = async (id) => {
        try {
            const blob = await exportDeviceRouteCsv(id);
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = `route_${id}.csv`;
            a.click();

            window.URL.revokeObjectURL(url);
        } catch {
            setError("Failed to export CSV.");
        }
    };

    if (loading) {
        return (
            <div className="p-6 text-slate-600">
                Loading IoT devices...
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        IoT Devices
                    </h1>
                    <p className="text-slate-600 mt-2">
                        Manage shipment IoT devices, live telemetry, GPS
                        position and route export.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={load}
                    className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                >
                    Refresh Devices
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Devices" value={stats.total} color="blue" />
                <StatCard title="Online" value={stats.online} color="green" />
                <StatCard title="Offline" value={stats.offline} color="red" />
                <StatCard title="GPS Active" value={stats.withGps} color="yellow" />
            </div>

            {error && (
                <div className="mb-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="mb-4 rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 mb-6">
                <h2 className="text-2xl font-bold mb-5">Add IoT Device</h2>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <input
                        placeholder="Tracking Number"
                        value={form.trackingNumber}
                        onChange={(e) =>
                            setForm({ ...form, trackingNumber: e.target.value })
                        }
                        className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <input
                        placeholder="Latitude"
                        value={form.latitude}
                        onChange={(e) =>
                            setForm({ ...form, latitude: e.target.value })
                        }
                        className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <input
                        placeholder="Longitude"
                        value={form.longitude}
                        onChange={(e) =>
                            setForm({ ...form, longitude: e.target.value })
                        }
                        className="px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <button
                        type="button"
                        onClick={handleCreate}
                        disabled={saving}
                        className="rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-700 transition disabled:opacity-60"
                    >
                        {saving ? "Saving..." : "Add Device"}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                <div className="p-6 border-b">
                    <h2 className="text-2xl font-bold">Device Registry</h2>
                    <p className="text-slate-500 mt-1">
                        Live IoT devices connected to logistics tracking.
                    </p>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-100">
                            <tr>
                                <th className="text-left px-4 py-3">ID</th>
                                <th className="text-left px-4 py-3">Tracking</th>
                                <th className="text-left px-4 py-3">Latitude</th>
                                <th className="text-left px-4 py-3">Longitude</th>
                                <th className="text-left px-4 py-3">Speed</th>
                                <th className="text-left px-4 py-3">Last Updated</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {devices.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan="8"
                                        className="text-center py-8 text-slate-500"
                                    >
                                        No IoT devices available.
                                    </td>
                                </tr>
                            ) : (
                                devices.map((d) => (
                                    <tr
                                        key={d.id}
                                        className="border-t hover:bg-slate-50"
                                    >
                                        <td className="px-4 py-3 font-medium">
                                            {d.id}
                                        </td>
                                        <td className="px-4 py-3 font-semibold">
                                            {d.trackingNumber || "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {d.latitude ?? "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {d.longitude ?? "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {d.stats?.speedKmh
                                                ? `${d.stats.speedKmh.toFixed(2)} km/h`
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            {d.lastUpdated
                                                ? new Date(d.lastUpdated).toLocaleString()
                                                : "—"}
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge online={d.isOnline} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                onClick={() => exportCsv(d.id)}
                                                className="rounded-lg bg-blue-600 px-3 py-2 text-white hover:bg-blue-700 transition"
                                            >
                                                CSV
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const StatCard = ({ title, value, color = "blue" }) => {
    const colors = {
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        red: "bg-red-50 text-red-700",
        yellow: "bg-yellow-50 text-yellow-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-3xl ${colors[color]}`}>
                {value}
            </div>
        </div>
    );
};

const StatusBadge = ({ online }) => (
    <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${online
                ? "bg-green-100 text-green-700"
                : "bg-red-100 text-red-700"
            }`}
    >
        {online ? "● Online" : "● Offline"}
    </span>
);

export default IoTDevicesPage;